// Path: src/components/aols/Estate/PropertyDetail.tsx
// Updated to remove FooterContainer and Footer, assuming global footer management
// Added fade-in animation to PropertyContent
// Enhanced SEO meta tags with OLS keywords and CTA
// Optimized for macOS 11 and mobile responsiveness (iPhone 12, 390x844)
// 06/04/25 03:38 PM +07

import React from "react";
import styled from "styled-components";
import { Typography, Button, Breadcrumb } from "antd";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import logo from "/logo.jpg";
import { InlineShareButtons } from "sharethis-reactjs";

const { Title: AntTitle, Paragraph: AntParagraph } = Typography;

interface Property {
  id: string;
  price: string;
  details: string;
  image: string;
  description: string;
  features: string[];
  location: string;
}

const PropertyDetailWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #000000 100%);
`;

const PropertyDetailLayout = styled.div`
  min-height: 100vh;
  background: transparent;
`;

const PropertyDetailSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  text-align: center;
`;

const LogoImg = styled.img`
  width: 150px;
  height: auto;
  margin-bottom: 20px;
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  margin-bottom: 20px;
  a {
    color: #ffaa00;
    text-decoration: none;
    &:hover {
      color: #ffcc00;
      text-decoration: underline;
    }
  }
  .ant-breadcrumb-link {
    color: #ffffff;
  }
`;

const PropertyContent = styled.div`
  display: flex;
  gap: 20px;
  background: #222;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: fadeIn 0.5s ease-in;
  @media (max-width: 768px) {
    flex-direction: column;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const PropertyImage = styled.img`
  width: 50%;
  height: 400px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  @media (max-width: 768px) {
    width: 100%;
    height: 300px;
  }
`;

const PropertyInfo = styled.div`
  width: 50%;
  text-align: left;
  color: #ddd;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PropertyTitle = styled(AntTitle)`
  && {
    color: #fff;
    margin-bottom: 15px;
    font-size: 2.5rem;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const PropertyPrice = styled(AntParagraph)`
  font-size: 1.5rem;
  font-weight: 600;
  color: #ffaa00;
  margin-bottom: 15px;
`;

const PropertyDescription = styled(AntParagraph)`
  font-size: 1.1rem;
  margin-bottom: 20px;
  line-height: 1.6;
  color: #ffffff;
`;

const FeaturesTitle = styled(AntTitle)`
  && {
    color: #fff;
    margin-bottom: 15px;
    font-size: 1.5rem;
  }
`;

const FeaturesList = styled.ul`
  list-style-type: disc;
  margin-left: 20px;
  margin-bottom: 20px;
  li {
    font-size: 1rem;
    margin: 5px 0;
    color: #ffffff;
  }
`;

const BuyButton = styled(Button)`
  background-color: #ffaa00;
  color: #0427f4;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 600;
  transition: background-color 0.3s ease, transform 0.3s ease;
  &:hover {
    background-color: #ffcc00;
    color: #0427f4;
    transform: scale(1.05);
  }
  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }
`;

const ShareThisWrapper = styled.div`
  margin-top: 20px;
`;

const NotFound = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #1a1a1a 0%, #000000 100%);
  color: #fff;
`;

const properties: Property[] = [
  {
    id: "mount-shasta-2.5-acres",
    price: "104,000 USD",
    details: "2.5 acres land, Mount Shasta, CA, USA",
    image:
      "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F104k.usd.2.5a.png?alt=media&token=d51a8d64-df56-45ef-8d60-2ff05c376fe1",
    description:
      "A serene 2.5-acre parcel nestled in the mystical Mount Shasta region, perfect for a retreat or meditation sanctuary. Offers stunning views and proximity to natural wonders.",
    features: ["2.5 acres", "Scenic views", "Quiet location", "Ideal for retreat"],
    location: "Mount Shasta, CA, USA",
  },
  {
    id: "red-sea-85-sqm",
    price: "54,000 USD",
    details: "85 sqm, 3 beds, Red Sea view, 200m walk",
    image:
      "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F54k.usd.2.7m.png?alt=media&token=402e5fb6-b1c3-49ae-9655-534fbc7d1b9a",
    description:
      "Spacious 85 sqm apartment with 3 bedrooms and a breathtaking Red Sea view. Just 200 meters from the beach, ideal for relaxation and coastal living.",
    features: ["85 sqm", "3 bedrooms", "Red Sea view", "200m to beach"],
    location: "Red Sea, Egypt",
  },
  {
    id: "red-sea-75-sqm",
    price: "39,000 USD",
    details: "75 sqm, 3 beds, Red Sea view",
    image:
      "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/Estate%2FPort%20Ghalib39k.png?alt=media&token=e62349d7-c6d3-416c-96df-e19af95a4a2e",
    description:
      "Cozy 75 sqm apartment featuring 3 bedrooms and a panoramic Red Sea view. Perfect for small families or vacation getaways.",
    features: ["75 sqm", "3 bedrooms", "Red Sea view", "Affordable"],
    location: "Red Sea, Egypt",
  },
  {
    id: "red-sea-54-sqm",
    price: "29,900 USD",
    details: "54 sqm, 1 bed, Red Sea view, w800",
    image:
      "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F28k.usd1.4m.png?alt=media&token=485902f0-c54a-4bf9-9b24-6b1616c41c51",
    description:
      "Compact 54 sqm unit with 1 bedroom and a stunning Red Sea view. Ideal for solo travelers or couples seeking a coastal escape.",
    features: ["54 sqm", "1 bedroom", "Red Sea view", "Coastal access"],
    location: "Red Sea, Egypt",
  },
  {
    id: "red-sea-150-sqm",
    price: "19,000 USD",
    details: "150 sqm, 3 beds, Red Sea view, w700",
    image:
      "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F18k.usd880egp.png?alt=media&token=58ca70bf-4b90-45f9-83f8-4bbe0b7a8b2c",
    description:
      "Expansive 150 sqm property with 3 bedrooms and Red Sea views. A budget-friendly option for larger families or investment.",
    features: ["150 sqm", "3 bedrooms", "Red Sea view", "Spacious"],
    location: "Red Sea, Egypt",
  },
  {
    id: "red-sea-135-sqm",
    price: "18,000 USD",
    details: "135 sqm, 3 beds, Red Sea view, w700",
    image:
      "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F17k.usd850egp.png?alt=media&token=c730abd0-c92f-4564-bc01-9af831e4728d",
    description:
      "Affordable 135 sqm apartment with 3 bedrooms and Red Sea views. Great value for a spacious coastal home.",
    features: ["135 sqm", "3 bedrooms", "Red Sea view", "Budget-friendly"],
    location: "Red Sea, Egypt",
  },
];

const PropertyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const property = properties.find((p) => p.id === id);

  React.useEffect(() => {
    console.log("PropertyDetail rendering for ID:", id);
    if (property) {
      console.log("Property found:", property.details);
    } else {
      console.log("Property not found for ID:", id);
    }
  }, [id, property]);

  if (!property) {
    return (
      <NotFound>
        <AntTitle level={2}>Property Not Found</AntTitle>
        <AntParagraph>
          The property you’re looking for doesn’t exist. Return to the{" "}
          <Link to="/estate">Estate Marketplace</Link>.
        </AntParagraph>
      </NotFound>
    );
  }

  return (
    <PropertyDetailWrapper>
      <PropertyDetailLayout>
        <Helmet>
          <title>{`${property.details} - OneLightSystem OLS Real Estate`}</title>
          <meta
            name="description"
            content={`Explore ${property.details} for ${property.price} at OneLightSystem OLS Marketplace. ${property.description} Join OLS meditation education at olsme.com/OLSStudentProspect!`}
          />
          <meta name="keywords" content={`OLS real estate, Sun Light Meditation, ${property.details}, Mount Shasta, Red Sea, meditation retreat, coastal property, join OLS`} />
          <meta name="author" content="OneLightSystem OLS" />
        </Helmet>
        <PropertyDetailSection>
          <LogoImg src={logo} alt="OneLightSystem OLS Logo" />
          <StyledBreadcrumb>
            <Breadcrumb.Item><Link to="/">Home</Link></Breadcrumb.Item>
            <Breadcrumb.Item><Link to="/estate">Real Estate</Link></Breadcrumb.Item>
            <Breadcrumb.Item>{property.details}</Breadcrumb.Item>
          </StyledBreadcrumb>
          <PropertyContent>
            <PropertyImage src={property.image} alt={property.details} />
            <PropertyInfo>
              <PropertyTitle level={1}>{property.details}</PropertyTitle>
              <PropertyPrice>{property.price}</PropertyPrice>
              <PropertyDescription>{property.description}</PropertyDescription>
              <FeaturesTitle level={3}>Features</FeaturesTitle>
              <FeaturesList>
                {property.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </FeaturesList>
              <BuyButton href="mailto:info@olsme.com" aria-label={`Buy ${property.details}`}>
                Buy Now
              </BuyButton>
              <ShareThisWrapper>
                <InlineShareButtons
                  config={{
                    alignment: "center",
                    color: "social",
                    enabled: true,
                    font_size: 16,
                    labels: "cta",
                    language: "en",
                    networks: ["whatsapp", "linkedin", "messenger", "facebook", "twitter", "blogger", "pinterest", "sms"],
                    padding: 12,
                    radius: 4,
                    show_total: true,
                    size: 20,
                    min_count: 10,
                    url: `https://www.olsme.com/estate/${property.id}`,
                    image: property.image,
                    description: `Check out this property: ${property.details} for $${property.price.replace(" USD", "")} USD at OneLightSystem OLS Marketplace! ${property.description}`,
                    title: `${property.details} - OneLightSystem OLS Real Estate`,
                    message: `Discover ${property.details} at OneLightSystem OLS Marketplace!`,
                    subject: `${property.details} - OLS Real Estate Listing`,
                    username: "onelightsystem",
                  }}
                />
              </ShareThisWrapper>
            </PropertyInfo>
          </PropertyContent>
        </PropertyDetailSection>
      </PropertyDetailLayout>
    </PropertyDetailWrapper>
  );
};

export default PropertyDetail;